import { MyAppeals } from "./Rewards.js";
import { useEffect, useRef, useState } from "react";
import type { JournalEntry, Whisper } from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";

const time = (iso: string) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/**
 * Private tab: whisper to the DM (answered in writing, only you see it) and your own journal.
 * Nobody else can read either: not other players, not the Host, not an Admin.
 */
export function PrivatePage() {
  const { role } = useGame();
  if (!role) return <main className="muted">Join this campaign to whisper to the DM and keep a journal.</main>;
  return (
    <main className="private-page">
      <p className="muted small">
        🔒 Only you can see this page. Whispers go to the DM alone and are answered here in writing. The DM keeps them (and your
        journal) in mind but never reveals them to the table unless you bring it up yourself. Hosts and Admins can't read them.
      </p>
      <MyAppeals />
      <Whispers />
      <Journal />
    </main>
  );
}

function Whispers() {
  const { campaign, fail, whisperVersion } = useGame();
  const [list, setList] = useState<Whisper[]>([]);
  const [canReply, setCanReply] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    api.whispers(campaign.id).then((r) => {
      setList(r.whispers);
      setCanReply(r.dmCanReply);
      if (r.whispers.at(-1)?.direction === "from_dm") setWaiting(false);
    }, fail);
  }, [campaign.id, whisperVersion, fail]);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "nearest" });
  }, [list.length]);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await api.whisper(campaign.id, t);
      setList((prev) => (prev.some((w) => w.id === r.whisper.id) ? prev : [...prev, r.whisper]));
      setText("");
      setWaiting(r.replying);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <h3>Whisper to the DM</h3>
      <div className="whisper-thread">
        {list.length === 0 && <p className="muted small">Ask the DM something privately, or tell them what your character does in secret.</p>}
        {list.map((w) => (
          <div key={w.id} className={`whisper ${w.direction === "to_dm" ? "mine" : "dm"}`}>
            <span className="muted small">{w.direction === "to_dm" ? "You" : "DM"} · {time(w.createdAt)}</span>
            <p className="prewrap">{w.text}</p>
          </div>
        ))}
        {waiting && <p className="muted small">The DM is writing back…</p>}
        <div ref={bottom} />
      </div>
      {!canReply && <p className="muted small">The DM can't answer in writing right now (no AI key on the server), but reads your whispers at the table.</p>}
      <form
        className="form-row"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          className="grow"
          rows={2}
          maxLength={2000}
          value={text}
          placeholder="e.g. While everyone argues, I quietly slip the silver key into my boot."
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button type="submit" className="primary" disabled={busy || !text.trim()}>
          Whisper
        </button>
      </form>
    </section>
  );
}

function Journal() {
  const { campaign, fail } = useGame();
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<{ id: number; title: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.journal(campaign.id).then((r) => setEntries(r.entries), fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <h3>My journal</h3>
      <div className="journal-form">
        <input value={title} maxLength={200} placeholder="Title (optional)" onChange={(e) => setTitle(e.target.value)} />
        <textarea rows={3} maxLength={20000} value={text} placeholder="Notes, suspicions, plans, your character's private thoughts…" onChange={(e) => setText(e.target.value)} />
        <button
          className="primary"
          disabled={busy || !text.trim()}
          onClick={() =>
            run(async () => {
              await api.addJournal(campaign.id, title.trim(), text.trim());
              setTitle("");
              setText("");
            })
          }
        >
          Add entry
        </button>
      </div>
      {entries === null && <p className="muted">Loading…</p>}
      {entries?.length === 0 && <p className="muted small">No entries yet.</p>}
      {entries?.map((j) =>
        editing?.id === j.id ? (
          <div key={j.id} className="journal-entry editing">
            <input value={editing.title} maxLength={200} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            <textarea rows={4} value={editing.text} maxLength={20000} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
            <div className="form-row">
              <button className="primary" disabled={busy || !editing.text.trim()} onClick={() => run(async () => { await api.updateJournal(campaign.id, j.id, editing.title, editing.text); setEditing(null); })}>
                Save
              </button>
              <button onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div key={j.id} className="journal-entry">
            <div className="row-between">
              <strong>{j.title || "Untitled"}</strong>
              <span className="muted small">
                {j.sessionNumber ? `Session ${j.sessionNumber} · ` : ""}
                {time(j.updatedAt)}
              </span>
            </div>
            <p className="prewrap">{j.text}</p>
            <div className="form-row">
              <button className="link small" onClick={() => setEditing({ id: j.id, title: j.title, text: j.text })}>Edit</button>
              <button className="link small" onClick={() => window.confirm("Delete this journal entry?") && run(() => api.deleteJournal(campaign.id, j.id))}>
                Delete
              </button>
            </div>
          </div>
        ),
      )}
    </section>
  );
}
